import { Link } from "react-router-dom";
import {
  Activity as ActivityIcon,
  AlertCircle,
  CreditCard,
  MessageSquare,
  MapPin,
  TrendingUp,
  UserPlus,
  Users,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { KpiCard } from "@/components/data/KpiCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingState } from "@/components/data/LoadingState";
import { ErrorState } from "@/components/data/ErrorState";
import { BarChart } from "@/components/charts/BarChart";
import { SparkLine } from "@/components/charts/SparkLine";
import { Badge } from "@/components/ui/badge";
import { useErrors, useOverview, useUsers, paginatedItems } from "@/lib/queries";
import { fmtNumber } from "@/lib/format";
import { RelTime } from "@/components/data/RelTime";
import { StatusBadge } from "@/components/data/StatusBadge";
import { UserAvatar } from "@/components/user/UserAvatar";
import { LocationBadge } from "@/components/location/LocationDisplay";

export function OverviewPage() {
  const overview = useOverview();
  const recentErrors = useErrors({ limit: 5 });
  const recentUsers = useUsers({ limit: 5, sort: "createdAt", dir: "desc" });

  if (overview.isLoading) return <LoadingState label="Loading overview…" />;
  if (overview.isError) return <ErrorState error={overview.error} onRetry={() => overview.refetch()} />;
  if (!overview.data) return null;
  const data = overview.data;

  const totalMessages24h = data.messages.telegram_24h + data.messages.whatsapp_24h;
  const totalMessages7d = data.messages.telegram_7d + data.messages.whatsapp_7d;
  const integrationTotals = data.integrations;
  const locations = data.locations ?? { precise_users_30d: 0, active_countries_30d: [], recent: [] };
  const intMix = (() => {
    const sum = integrationTotals.has_ios_token + integrationTotals.telegram_users + integrationTotals.whatsapp_users;
    if (sum === 0) return [];
    return [
      { label: "iOS", count: integrationTotals.has_ios_token, color: "bg-sky-500" },
      { label: "Telegram", count: integrationTotals.telegram_users, color: "bg-cyan-500" },
      { label: "WhatsApp", count: integrationTotals.whatsapp_users, color: "bg-emerald-500" },
    ].map((s) => ({ ...s, pct: sum > 0 ? s.count / sum : 0 }));
  })();

  const errorRows = paginatedItems(recentErrors.data);
  const userRows = recentUsers.data?.users ?? [];

  return (
    <>
      <PageHeader
        eyebrow="god mode"
        title="Overview"
        description="Live state across users, expenses, errors, and integrations."
      />

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <KpiCard
          label="Total users"
          value={fmtNumber(data.users.total)}
          hint={`${data.users.banned} banned`}
          icon={<Users className="h-4 w-4" />}
        />
        <KpiCard
          label="DAU"
          value={fmtNumber(data.users.active_today)}
          hint={`${data.users.signups_24h} signups (24h)`}
          icon={<UserPlus className="h-4 w-4" />}
        />
        <KpiCard
          label="WAU"
          value={fmtNumber(data.users.active_7d)}
          hint="Activity logs keep 7 days"
          icon={<ActivityIcon className="h-4 w-4" />}
        />
        <KpiCard
          label="Expenses (24h)"
          value={fmtNumber(data.expenses.last_24h)}
          hint={`${fmtNumber(data.expenses.last_30d)} in 30d`}
          icon={<CreditCard className="h-4 w-4" />}
          accent="info"
        />
        <KpiCard
          label="Errors (24h)"
          value={fmtNumber(data.errors.last_24h)}
          hint={`${fmtNumber(data.errors.last_7d)} in 7d`}
          icon={<AlertCircle className="h-4 w-4" />}
          accent={data.errors.last_24h > 0 ? "danger" : "default"}
        />
        <KpiCard
          label="Messages (24h)"
          value={fmtNumber(totalMessages24h)}
          hint={`${fmtNumber(totalMessages7d)} in 7d`}
          icon={<MessageSquare className="h-4 w-4" />}
          accent="info"
        />
      </section>

      <section className="mt-6 grid gap-4 xl:grid-cols-3">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Signups</CardTitle>
              <Badge variant="outline" className="font-mono">30d</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <BarChart data={data.series.signups_30d} color="#38bdf8" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Expenses logged</CardTitle>
              <Badge variant="outline" className="font-mono">30d</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <BarChart data={data.series.expenses_30d} color="#38bdf8" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Errors</CardTitle>
              <Badge variant={data.errors.last_24h > 0 ? "error" : "outline"} className="font-mono">30d</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <SparkLine data={data.series.errors_30d} color="#fb7185" height={180} />
          </CardContent>
        </Card>
      </section>

      <section className="mt-6 grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Locations</CardTitle>
              <Badge variant="outline" className="font-mono">7d</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Precise" value={locations.precise_users_30d} tone="success" />
              <Stat label="Countries" value={locations.active_countries_30d.length} tone="info" />
            </div>
            <ul className="space-y-1.5 text-xs">
              {locations.active_countries_30d.slice(0, 5).map((c) => (
                <li key={c.country} className="flex items-center justify-between gap-3">
                  <span className="inline-flex min-w-0 items-center gap-2">
                    <MapPin className="h-3 w-3 shrink-0 text-sky-300" />
                    <span className="truncate font-mono">{c.country}</span>
                  </span>
                  <span className="tabular-nums text-muted-foreground">
                    {fmtNumber(c.users)} users · {fmtNumber(c.count)} req
                  </span>
                </li>
              ))}
              {locations.active_countries_30d.length === 0 && (
                <li className="text-muted-foreground">No location data yet</li>
              )}
            </ul>
            {locations.recent.length > 0 ? (
              <div className="space-y-1 border-t border-border/40 pt-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Recent precise</div>
                {locations.recent.slice(0, 3).map((loc) => (
                  <div key={loc.id ?? `${loc.user_id}-${loc.observed_at}`} className="flex min-w-0 items-center justify-between gap-2">
                    <LocationBadge location={loc} />
                    {loc.user_id ? (
                      <Link to={`/users/${loc.user_id}`} className="truncate text-[11px] text-muted-foreground hover:text-foreground">
                        {loc.user_email ?? loc.user_id.slice(0, 8)}
                      </Link>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Integration mix</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-secondary">
              {intMix.map((s) => (
                <div
                  key={s.label}
                  className={s.color}
                  style={{ width: `${s.pct * 100}%` }}
                  title={`${s.label}: ${s.count} (${(s.pct * 100).toFixed(0)}%)`}
                />
              ))}
            </div>
            <ul className="space-y-1.5 text-xs">
              {intMix.map((s) => (
                <li key={s.label} className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${s.color}`} />
                    {s.label}
                  </span>
                  <span className="tabular-nums text-muted-foreground">
                    {fmtNumber(s.count)} ({(s.pct * 100).toFixed(0)}%)
                  </span>
                </li>
              ))}
              {intMix.length === 0 && <li className="text-muted-foreground">No integration data yet</li>}
            </ul>
            <div className="grid grid-cols-3 gap-2 pt-2">
              <Stat label="iOS" value={integrationTotals.has_ios_token} tone="info" />
              <Stat label="TG" value={integrationTotals.telegram_users} tone="info" />
              <Stat label="WA" value={integrationTotals.whatsapp_users} tone="info" />
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent errors</CardTitle>
              <Link to="/errors" className="text-xs text-muted-foreground hover:text-foreground">
                View all →
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {recentErrors.isLoading && <LoadingState />}
            {recentErrors.isError && <ErrorState error={recentErrors.error} onRetry={() => recentErrors.refetch()} />}
            {errorRows.length === 0 && !recentErrors.isLoading && !recentErrors.isError && (
              <p className="py-6 text-center text-xs text-muted-foreground">
                <TrendingUp className="mx-auto mb-2 h-4 w-4 text-emerald-400" />
                No errors recently. Nice.
              </p>
            )}
            <ul className="divide-y divide-border/40">
              {errorRows.map((e) => (
                <li key={e.id} className="py-2">
                  <Link to={`/errors?path=${encodeURIComponent(e.path ?? "")}`} className="block hover:bg-secondary/40 -mx-2 px-2 rounded">
                    <div className="flex items-center gap-2 text-xs">
                      <StatusBadge status={e.status} />
                      <span className="truncate font-mono text-foreground/80">{e.method ?? ""} {e.path ?? "—"}</span>
                      <span className="ml-auto text-muted-foreground">
                        <RelTime value={e.created_at} />
                      </span>
                    </div>
                    {e.message && <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{e.message}</p>}
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent signups</CardTitle>
              <Link to="/users" className="text-xs text-muted-foreground hover:text-foreground">
                View all →
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentUsers.isLoading && <LoadingState />}
            {recentUsers.isError && <ErrorState error={recentUsers.error} onRetry={() => recentUsers.refetch()} />}
            <ul className="divide-y divide-border/40">
              {userRows.map((u) => (
                <li key={u.id}>
                  <Link to={`/users/${u.id}`} className="-mx-2 flex items-center gap-3 rounded px-2 py-2 hover:bg-secondary/40">
                    <UserAvatar size="sm" name={u.name} email={u.email} image={u.image} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{u.name ?? u.email}</div>
                      <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                    </div>
                    <span className="text-[11px] text-muted-foreground">
                      <RelTime value={u.createdAt} />
                    </span>
                  </Link>
                </li>
              ))}
              {userRows.length === 0 && !recentUsers.isLoading && !recentUsers.isError && (
                <li className="py-6 text-center text-xs text-muted-foreground">No recent signups</li>
              )}
            </ul>
          </CardContent>
        </Card>
      </section>
    </>
  );
}

function Stat({ label, value, tone = "info" }: { label: string; value: number; tone?: "info" | "success" | "warn" }) {
  const color = tone === "success" ? "text-emerald-300" : tone === "warn" ? "text-amber-300" : "text-sky-300";
  return (
    <div className="rounded-md border border-border/40 bg-secondary/40 px-2 py-1.5 text-center">
      <div className={`text-base font-semibold tabular-nums ${color}`}>{fmtNumber(value)}</div>
      <div className="mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}
